<template>
  <FadeTransition>
    <SettingsPagePanel>
      <SettingsPagePanelHeader>
        <template #title>{{ title }}</template>
        <template #right>
          <div v-if="isEditing" :class="$style.buttonGroup">
            <FlatButton
              variant="secondary"
              size="medium"
              @click="handleCancelButtonClick"
            >
              キャンセル
            </FlatButton>
            <FlatButton
              variant="primary"
              size="medium"
              @click="handleButtonClick"
            >
              保存
            </FlatButton>
          </div>
          <template v-else>
            <FlatButton
              variant="primary"
              size="medium"
              @click="handleButtonClick"
            >
              <template #icon>
                <IconPen />
              </template>
              編集
            </FlatButton>
          </template>
        </template>
      </SettingsPagePanelHeader>
      <SettingsPageAutoPostKartePlannerCommentDescription />
      <SettingsTable :isEditing="isEditing">
        <SettingsTableRow name="有効かどうか">
          <p>{{ enabledString }}</p>
          <template #afterName>
            <template v-if="!canBeEnabled">
              <HelpPoptip :class="$style.title__poptip">
                <p :class="$style.tips">
                  プロフィールタブの自己紹介文とプロフィール画像を設定していただくと有効にすることができます。
                </p>
              </HelpPoptip>
            </template>
          </template>
          <template #editor>
            <ul :class="$style.options">
              <li :class="$style.options__item">
                <label :class="$style.option">
                  <DolphinRadioButton
                    v-model="formValues.enabled"
                    value="enabled"
                    :disabled="!canBeEnabled"
                    name="enabled"
                  />
                  <span :class="$style.option__text">有効</span>
                </label>
              </li>
              <li :class="$style.options__item">
                <label :class="$style.option">
                  <DolphinRadioButton
                    v-model="formValues.enabled"
                    value="disabled"
                    :disabled="!canBeEnabled"
                    name="enabled"
                  />
                  <span :class="$style.option__text">無効</span>
                </label>
              </li>
            </ul>
          </template>
        </SettingsTableRow>

        <SettingsTableRow name="自動アプローチの内容">
          <div>
            <p :class="$style.comment">{{ setting.comment }}</p>
            <ul :class="$style.view__selection">
              <li
                v-for="(selection, index) in setting.selections"
                :key="index"
                :class="$style.view__selectionItem"
              >
                {{ selection }}
              </li>
            </ul>
          </div>
          <template #editor>
            <div :class="$style.commentEditor">
              <p :class="[$style.commentEditor__replace, $style.replace]">
                💡 メッセージ中の<code>{{ '\{\{名前\}\}' }}</code
                >という文字列が、ユーザーの名前に置き換えられます。
              </p>
              <CountingTextarea
                placeholder="例）はじめまして！"
                :rows="12"
                v-model="formValues.comment"
              />
              <div>
                <div :class="$style.selection">
                  <ul>
                    <li
                      v-for="(selection, index) in formValues.selections"
                      :key="index"
                    >
                      <DolphinTextInput
                        type="text"
                        :class="$style.selection__input"
                        :modelValue="selection"
                        @update:modelValue="
                          value => updateSelectionValue(String(value), index)
                        "
                      />
                      <ButtonClose
                        v-if="index >= 2"
                        @click="removeSelection(index)"
                      />
                    </li>
                  </ul>
                  <button
                    :class="$style.selection__append"
                    @click="appendSelection"
                  >
                    ＋追加
                  </button>
                </div>
              </div>
            </div>
          </template>
        </SettingsTableRow>
      </SettingsTable>

      <component :is="includesUserNameModal.ControlledModal">
        <KartePlannerCommentIncludesUserNameConfirmation
          :maybe-user-names="includesUserNames"
          @submit="updateSetting"
          @cancel="includesUserNameModal.closeModal()"
        />
      </component>
    </SettingsPagePanel>
  </FadeTransition>
</template>

<script setup lang="ts">
import IconPen from '@/components/icons/IconPen.vue'
import ButtonClose from '@/components/atoms/ButtonClose.vue'
import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import CountingTextarea from '@/components/molecules/CountingTextarea.vue'
import HelpPoptip from '@/components/molecules/HelpPoptip.vue'
import KartePlannerCommentIncludesUserNameConfirmation from '@/components/organisms/KartePlannerCommentIncludesUserNameConfirmation/KartePlannerCommentIncludesUserNameConfirmation.vue'
import FadeTransition from '@/components/transitions/FadeTransition.vue'
import { useModal } from '@/hooks/useModal'
import { useAlert } from '@/libs/alertPortal'
import SettingsTableRow from '@/pages/SettingsPage/SettingsTable/SettingsTableRow.vue'
import SettingsTable from '@/pages/SettingsPage/SettingsTable/SettingsTable.vue'
import router from '@/router'
import { computed, ref, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import SettingsPageAutoPostKartePlannerCommentDescription from './SettingsPageAutoPostKartePlannerCommentDescription.vue'
import {
  type SettingsPageAutoPostKartePlannerCommentViewViewerFragment,
  usePreviewAutoPostKartePlannerCommentSettingMutation,
  useUpdateSettingsPageAutoPostKartePlannerCommentMutation,
} from './SettingsPageAutoPostKartePlannerCommentView.generated'
import { useLogger } from '@/logger/logger'

const props = defineProps<{
  viewer: SettingsPageAutoPostKartePlannerCommentViewViewerFragment
}>()

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: { onConfirm: () => void; onAbort: () => void }
  ): void
}>()

if (!props.viewer.autoPostKartePlannerComment.available) {
  // eslint-disable-next-line fp/no-mutating-methods
  router.push({ name: 'errorNotFound' })
}

const includesUserNameModal = useModal()

const isEditing = ref(false)
const includesUserNames = ref<string[]>([])

const enabledString = computed(() => (setting.value.enabled ? '有効' : '無効'))
const setting = computed(() => {
  const { setting } = props.viewer.autoPostKartePlannerComment
  if (!setting) {
    throw new Error('setting is null')
  }
  return setting
})
const canBeEnabled = computed(
  () => props.viewer.profile.comment.length > 0 && !!props.viewer.profile.avatar
)
const title = computed(() =>
  isEditing.value ? '自動アプローチ | 編集中' : '自動アプローチ'
)

type Form = {
  enabled: 'enabled' | 'disabled'
  comment: string
  selections: string[]
}

const formValues = ref<Form>({
  enabled: setting.value.enabled ? 'enabled' : 'disabled',
  comment: setting.value.comment,
  selections: setting.value.selections,
})

watch(
  () => props.viewer.autoPostKartePlannerComment,
  () => {
    formValues.value = {
      enabled: setting.value.enabled ? 'enabled' : 'disabled',
      comment: setting.value.comment,
      selections: setting.value.selections,
    }
  }
)

onBeforeRouteLeave((to, from, next) => {
  if (!isEditing.value) {
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      next()
    },
    onAbort: () => next(false),
  })
})

const updateSelectionValue = (text: string, index: number) => {
  const selections = formValues.value.selections.map((s, i) =>
    i === index ? text : s
  )
  formValues.value.selections = selections
}

const removeSelection = (index: number) => {
  formValues.value.selections = formValues.value.selections.filter(
    (_, i) => i !== index
  )
}

const appendSelection = () => {
  formValues.value.selections = [...formValues.value.selections, '']
}

const { mutate: previewAutoPostKartePlannerCommentSetting } =
  usePreviewAutoPostKartePlannerCommentSettingMutation({})

const updateSettingsPageAutoPostKartePlannerComment =
  useUpdateSettingsPageAutoPostKartePlannerCommentMutation({})

const alert = useAlert()
const logger = useLogger()

const handleButtonClick = async () => {
  if (!isEditing.value) {
    isEditing.value = true
    return
  }

  const selections = formValues.value.selections
    .map(s => s.trim())
    .filter(s => s !== '')

  if (formValues.value.comment === '') {
    alert.warning('コメントを入力してください。')
    return
  }

  if (selections.length < 2) {
    alert.warning('選択肢は2つ以上入力してください。')
    return
  }

  const enabled = formValues.value.enabled === 'enabled'

  const input = {
    enabled,
    comment: formValues.value.comment,
    selections: selections,
  }

  const previewRes = await previewAutoPostKartePlannerCommentSetting({
    setting: input,
  })
  if (!previewRes?.data) {
    throw new Error('Failed to previewAutoPostKartePlannerCommentSetting')
  }

  includesUserNames.value =
    previewRes.data.previewAutoPostKartePlannerCommentSetting.maybeUserNames

  if (includesUserNames.value.length > 0) {
    includesUserNameModal.openModal()
    return
  }

  await updateSetting()

  alert.success('自動アプローチの設定を更新しました。')
}

const updateSetting = async () => {
  const selections = formValues.value.selections
    .map(s => s.trim())
    .filter(s => s !== '')
  const enabled = formValues.value.enabled === 'enabled'

  if (formValues.value.comment === '') {
    alert.warning('コメントを入力してください。')
    return
  }

  if (selections.length < 2) {
    alert.warning('選択肢は2つ以上入力してください。')
    return
  }

  try {
    await updateSettingsPageAutoPostKartePlannerComment.mutate({
      input: {
        enabled,
        comment: formValues.value.comment,
        selections: selections,
      },
    })

    await logger.ltlPeople.event.updateAutoPostKartePlannerCommentSetting(
      logger.buildMeta(),
      {
        data: {
          enabled,
          comment: formValues.value.comment,
          selections,
        },
      }
    )
  } catch {
    alert.error('更新処理中にエラーが発生しました。')
  } finally {
    isEditing.value = false
    includesUserNameModal.closeModal()
  }
}

const handleCancelButtonClick = () => {
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      isEditing.value = false
    },
    onAbort: () => {},
  })

  const { setting } = props.viewer.autoPostKartePlannerComment
  if (!setting) return

  formValues.value = {
    enabled: setting.enabled ? 'enabled' : 'disabled',
    comment: setting.comment,
    selections: setting.selections,
  }
}
</script>

<style lang="scss" module>
.buttonGroup {
  display: flex;
  gap: 16px;
}

.comment {
  white-space: pre-wrap;
}

.options {
  $gutter-horizontal: 40px;
  $gutter-vertical: 24px;

  display: flex;
  flex-wrap: wrap;
  margin-top: -$gutter-vertical;
  margin-left: -$gutter-horizontal;

  &__item {
    margin-top: $gutter-vertical;
    margin-left: $gutter-horizontal;
  }
}

.option {
  display: inline-flex;
  align-items: center;

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.view {
  &__selection {
    margin-top: 16px;
  }

  &__selectionItem {
    list-style: circle;
  }
}

.selection {
  margin-top: 16px;

  li {
    display: flex;
    align-items: center;

    & > *:nth-child(n + 2) {
      margin-left: 8px;
    }

    &:nth-child(n + 2) {
      margin-top: 10px;
    }
  }

  &__input {
    width: 90%;
  }

  &__append {
    margin-top: 8px;
    color: $color-text-and-object-accent-primary;
    line-height: 2;
  }
}

.commentEditor {
  &__replace {
    margin-bottom: 16px;
  }
}

.title {
  &__poptip {
    &:nth-child(n + 2) {
      margin-left: 8px;
    }
  }
}

.tips {
  overflow-wrap: break-word;
  word-wrap: break-word;
  width: 200px;
}

.replace {
  align-self: flex-start;
  padding: 16px;
  background-color: $color-surface-secondary;
  border-radius: 8px;
  color: $color-text-and-object-medium-emphasis;
  font-size: 14px;
  line-height: 24px;

  &__title {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-weight: bold;
  }

  code {
    padding: 0.2em 0.4em;
    background-color: $color-surface-tertiary;
    border-radius: 4px;
    font-size: 14px;
  }
}
</style>
